import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate, useParams } from 'react-router-dom';
import { tokens } from '../../theme';
import useClassService from '../../api/services/ClassService';
import useAnnouncementService from '../../api/services/announcementService';
import Table from '../../components/Table';
import LoadingSpinner from '../../components/LoadingSpinner';
import CreateSmsDialog from './CreateSmsDialog';
import EditSmsDialog from './EditSmsDialog';
import DeleteSmsDialog from './DeleteSmsDialog';
import { getUserFromCookies } from '../../utils/Cookie-utils';

const MANAGE_ROLES = ['ADMIN', 'ACADEMIC', 'SECRETARY', 'ACCOUNTANT'];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SENT':
      return 'info';
    case 'PUBLISHED':
      return 'info';
    case 'DISABLED':
      return 'default';
    default:
      return 'warning';
  }
};

const AnnouncementCard = ({
  announcement,
  colors,
  canManage,
  onEdit,
  onDelete,
  onSendSms,
  isSending,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const cardBg = isDark ? colors.primary[400] : colors.primary[600];
  const titleColor = '#ffffff';
  const descColor = isDark ? colors.grey[200] : '#e8eef8';
  const metaColor = isDark ? colors.grey[300] : '#c5d0e6';
  const messagePanelBg = isDark ? alpha('#ffffff', 0.14) : '#ffffff';
  const messageTextColor = isDark ? '#ffffff' : colors.primary[600];
  const messageLabelColor = isDark ? colors.greenAccent[300] : colors.blueAccent[600];

  return (
  <Card
    sx={{
      width: { xs: '100%', sm: 300 },
      minHeight: 260,
      borderRadius: '16px',
      backgroundColor: cardBg,
      border: `1px solid ${alpha('#ffffff', isDark ? 0.14 : 0.22)}`,
      boxShadow: isDark
        ? '0 8px 22px rgba(0,0,0,0.28)'
        : '0 10px 24px rgba(31, 42, 64, 0.22)',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box display="flex" alignItems="flex-start" gap={1.25} flex={1} minWidth={0}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(colors.blueAccent[500], 0.28),
              flexShrink: 0,
            }}
          >
            <CampaignOutlined sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: titleColor, lineHeight: 1.3 }}
            title={announcement.messageTitle}
          >
            {announcement.messageTitle}
          </Typography>
        </Box>
        {canManage && (
          <Box display="flex" gap={0.25} flexShrink={0}>
            <Tooltip title="Edit SMS">
              <IconButton
                size="small"
                onClick={() => onEdit(announcement)}
                sx={{
                  color: '#ffffff',
                  bgcolor: alpha('#ffffff', 0.1),
                  p: 0.6,
                  '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
                }}
              >
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete SMS">
              <IconButton
                size="small"
                onClick={() => onDelete(announcement)}
                sx={{
                  color: colors.redAccent[400],
                  bgcolor: alpha(colors.redAccent[500], 0.18),
                  p: 0.6,
                  '&:hover': { bgcolor: alpha(colors.redAccent[500], 0.3) },
                }}
              >
                <DeleteIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {announcement.messageDescription && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: descColor,
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.45,
          }}
        >
          {announcement.messageDescription}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1.25,
          p: 1.25,
          borderRadius: '12px',
          bgcolor: messagePanelBg,
          border: `1px solid ${alpha(isDark ? '#ffffff' : colors.primary[600], isDark ? 0.16 : 0.12)}`,
          flex: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: messageLabelColor,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 800,
            fontSize: '0.68rem',
          }}
        >
          SMS Message
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            color: messageTextColor,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.92rem',
            fontWeight: isDark ? 500 : 600,
          }}
        >
          {announcement.messageBody}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.25} gap={1}>
        <Box>
          <Chip
            label={announcement.status || 'ACTIVE'}
            size="small"
            color={getStatusColor(announcement.status)}
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#fff',
            }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: metaColor, fontWeight: 500 }}>
            {formatDate(announcement.createdAt)}
          </Typography>
        </Box>
      </Box>

      {canManage && announcement.status !== 'SENT' && (
        <Button
          variant="contained"
          size="small"
          fullWidth
          startIcon={<SendIcon />}
          onClick={() => onSendSms(announcement)}
          disabled={isSending}
          sx={{
            mt: 1.5,
            textTransform: 'none',
            fontSize: '0.85rem',
            fontWeight: 700,
            py: 0.9,
            bgcolor: isDark ? colors.greenAccent[600] : colors.greenAccent[400],
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: isDark ? colors.greenAccent[500] : colors.greenAccent[500],
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          }}
        >
          {isSending ? 'Sending...' : 'SEND SMS'}
        </Button>
      )}
    </CardContent>
  </Card>
  );
};

const ClassAnnouncementsView = ({ classId, colors, onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headerTitleColor = isDark ? colors.grey[100] : colors.primary[600];
  const headerSubColor = isDark ? colors.grey[300] : colors.grey[500];

  const { getClassById } = useClassService();
  const {
    getAnnouncementsByClass,
    deleteAnnouncement,
    sendAnnouncementSms,
    isLoading: isAnnouncementLoading,
  } = useAnnouncementService();

  const user = getUserFromCookies();
  const userRole = user?.data?.role;
  const canManage = MANAGE_ROLES.includes(userRole);

  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [sendingSmsId, setSendingSmsId] = useState(null);

  const loadClass = async () => {
    try {
      const data = await getClassById(classId);
      setClassData(data);
    } catch (error) {
      console.error('Failed to load class:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const data = await getAnnouncementsByClass(classId);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load announcements:', error);
      setAnnouncements([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!classId) return;
      setLoading(true);
      await Promise.all([loadClass(), loadAnnouncements()]);
      setLoading(false);
    };
    init();
  }, [classId]);

  const handleRefresh = async () => {
    await loadAnnouncements();
  };

  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setEditOpen(true);
  };

  const handleDelete = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedAnnouncement?.id) return;
    await deleteAnnouncement(selectedAnnouncement.id, async () => {
      setDeleteOpen(false);
      setSelectedAnnouncement(null);
      await handleRefresh();
    });
  };

  const handleSendSms = async (announcement) => {
    if (!announcement?.id || announcement.status === 'SENT') return;
    try {
      setSendingSmsId(announcement.id);
      await sendAnnouncementSms(announcement.id);
      await handleRefresh();
    } catch (error) {
      console.error('Failed to send SMS:', error);
    } finally {
      setSendingSmsId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading announcements..." height="300px" />;
  }

  return (
    <Box p={2} ml={2} mr={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: headerTitleColor }}>
            Announcements
          </Typography>
          <Typography variant="body2" sx={{ color: headerSubColor, fontWeight: 500 }}>
            {classData?.className || classData?.classTitle || 'Class'}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {canManage && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Create SMS
            </Button>
          )}
          <Button variant="outlined" color="secondary" onClick={onBack} sx={{ textTransform: 'none' }}>
            Back to classes
          </Button>
        </Box>
      </Box>

      {isAnnouncementLoading ? (
        <LoadingSpinner message="Loading SMS announcements..." height="240px" />
      ) : announcements.length > 0 ? (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="stretch"
        >
          {announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              colors={colors}
              canManage={canManage}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSendSms={handleSendSms}
              isSending={sendingSmsId === announcement.id}
            />
          ))}
        </Box>
      ) : (
        <Box
          p={4}
          textAlign="center"
          sx={{
            borderRadius: '18px',
            backgroundColor: isDark ? colors.primary[400] : colors.primary[600],
            border: `1px solid ${alpha('#ffffff', 0.12)}`,
          }}
        >
          <CampaignOutlined sx={{ fontSize: 48, color: alpha('#ffffff', 0.7), mb: 1 }} />
          <Typography sx={{ color: '#ffffff' }} fontWeight={600}>
            No announcements have been posted for this class yet.
          </Typography>
          {canManage && (
            <Typography variant="body2" sx={{ mt: 1, color: alpha('#ffffff', 0.82) }}>
              Click &quot;Create SMS&quot; to publish the first announcement.
            </Typography>
          )}
        </Box>
      )}

      <CreateSmsDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        classId={classId}
        onSuccess={handleRefresh}
      />

      <EditSmsDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedAnnouncement(null);
        }}
        announcement={selectedAnnouncement}
        onSuccess={handleRefresh}
      />

      <DeleteSmsDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedAnnouncement(null);
        }}
        announcement={selectedAnnouncement}
        onConfirm={confirmDelete}
        isLoading={isAnnouncementLoading}
      />
    </Box>
  );
};

const OngoingClassesList = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { classes, totalRecords, fetchClasses } = useClassService();

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadClasses();
  }, [page]);

  const loadClasses = async () => {
    setIsLoading(true);
    await fetchClasses(page, size, 'ONGOING');
    setIsLoading(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openClassAnnouncements = (classId) => {
    navigate(`/announcements/${classId}`);
  };

  const filteredClasses = useMemo(() => {
    if (!classes?.length) return [];
    if (!searchQuery.trim()) return classes;

    const query = searchQuery.toLowerCase();
    return classes.filter((clazz) => {
      const className = (clazz.className || '').toLowerCase();
      const classTitle = (clazz.classTitle || '').toLowerCase();
      const classType = (clazz.classType || '').toLowerCase();

      return (
        className.includes(query) ||
        classTitle.includes(query) ||
        classType.includes(query)
      );
    });
  }, [classes, searchQuery]);

  const columns = [
    { field: 'className', headerName: 'Class_Name', flex: 1, minWidth: 130 },
    { field: 'classTitle', headerName: 'Class_Title', flex: 1, minWidth: 130 },
    { field: 'classType', headerName: 'Class_type', flex: 1, minWidth: 130 },
    { field: 'classTeacher', headerName: 'Class_teacher', flex: 1, minWidth: 130 },
    { field: 'createdAt', headerName: 'CreatedAt', flex: 1, minWidth: 150 },
    { field: 'status', headerName: 'Status', flex: 1, minWidth: 100 },
    {
      field: 'viewAnnouncements',
      headerName: 'Announcements',
      width: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            sx={{ padding: '5px 8px', fontSize: '13px', textTransform: 'none' }}
            onClick={() => openClassAnnouncements(row.id)}
          >
            View announcements
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="0px">
      <Box p={2} ml={2} mr={2}>
        {isSmallScreen && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search ongoing classes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: 1,
              }}
            />
          </Box>
        )}

        {isLoading ? (
          <LoadingSpinner message="Loading ongoing classes..." height="300px" />
        ) : isSmallScreen ? (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredClasses.length > 0 ? (
              filteredClasses.map((clazz) => (
                <Card
                  key={clazz.id}
                  sx={{
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: colors.primary[400],
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight="bold" color={colors.grey[100]}>
                      {clazz.classTitle || clazz.className}
                    </Typography>
                    <Typography variant="caption" color={colors.grey[300]}>
                      {clazz.className} • {clazz.classType} • {clazz.status}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      size="small"
                      sx={{ mt: 1, textTransform: 'none' }}
                      onClick={() => openClassAnnouncements(clazz.id)}
                    >
                      View announcements
                    </Button>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography color={colors.grey[100]} textAlign="center" py={4}>
                No ongoing classes found.
              </Typography>
            )}
          </Box>
        ) : (
          <Table
            rows={classes}
            columns={columns}
            totalRecords={totalRecords}
            size={size}
            page={page}
            handlePageChange={handlePageChange}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
};

const Announcement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { classId } = useParams();

  if (classId) {
    return (
      <ClassAnnouncementsView
        classId={classId}
        colors={colors}
        onBack={() => navigate('/announcements')}
      />
    );
  }

  return <OngoingClassesList />;
};

export default Announcement;
